<template>
    <drop-down
        :should-close="shouldCloseDropdown"
        @closeDropdown="(val) => (this.shouldCloseDropdown = val)"
        @closeOtherDropDown="closeOtherDropDown"
    >
        <span
            class="status"
            slot="title"
            data-toggle="dropdown"
            :class="[
                `issue-status-${getStatusClassName(issue.issueStatusId)}`,
                statusOptions.length ? '' : 'no-pointer',
                shouldShowMobileStyle ? 'mobile-status' : ''
            ]"
        >
            <span v-if="!shouldShowMobileStyle">{{ getStatusName(issue.issueStatusId) }}</span>
            <span v-else>{{ getStatusName(issue.issueStatusId).charAt(0) }}</span>
        </span>
        <ul class="dropdown-menu" :class="{ 'mobile-dropdown': shouldShowMobileStyle }">
            <li v-for="(status, index) in statusOptions" :key="index">
                <a @click.stop="changeStatus(status)">
                    {{ status.statusName }}
                </a>
            </li>
        </ul>
    </drop-down>
</template>

<script>
import { mapMutations } from 'vuex';
import { ISSUE_STATUSES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins';

export default {
    name: 'IssueStatusButton',
    props: {
        issue: {
            type: Object,
            required: true
        },
        showMobileStyle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            statusOptions: ISSUE_STATUSES,
            shouldCloseDropdown: false
        };
    },
    computed: {
        isMobileView() {
            return window.innerWidth <= 768 || this.$root.isMobileOnly;
        },
        shouldShowMobileStyle() {
            return this.isMobileView && this.showMobileStyle;
        }
    },
    mixins: [GeneralMixin],
    methods: {
        ...mapMutations({
            incrementIssues: 'team/INCREMENT_PENDING_ISSUES',
            decrementIssues: 'team/DECREMENT_PENDING_ISSUES'
        }),
        getStatusClass(status) {
            const statusLower = status.toLowerCase().replace(' ', '-');
            return `issue-status-${statusLower}`;
        },
        async changeStatus(newStatus) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Change Status',
                    body: `Are you sure you want to change the status from "${this.issue.statusName}" to "${
                        newStatus.statusName
                    }"?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        try {
                            const tempOldStatusId = this.issue.issueStatusId;

                            const payload = {
                                method: 'post',
                                data: {
                                    title: this.issue.title,
                                    issueId: this.issue.issueId,
                                    issueStatusId: newStatus.issueStatusId,
                                    teamId: this.issue.teamId,
                                    tripDate: this.issue.tripDate,
                                    createdByUserId: this.issue.createdByUserId,
                                    createdDate: this.issue.createdDate,
                                    lastModifiedByUserId: this.issue.lastModifiedByUserId,
                                    lastModifiedDate: this.issue.lastModifiedDate,
                                    issueReference: this.issue.issueReference
                                }
                            };

                            this.shouldCloseDropdown = true;
                            this.$_handleLoaderState(true, '...UPDATING');

                            const api = `/api/issue/update-issue`;
                            const response = await handleRequests(api, payload);

                            this.$emit('statusUpdated', {
                                issueId: this.issue.issueId,
                                issueStatusId: newStatus.issueStatusId,
                                lastModifiedDate: response.data[0].lastModifiedDate,
                                lastModifiedByUserId: response.data[0].lastModifiedByUserId,
                                lastModifiedByName: response.data[0].lastModifiedByName
                            });

                            this.$notify({
                                message: `Status changed to ${newStatus.statusName}`,
                                type: 'success'
                            });

                            if (
                                this.statusOptions.find((x) => x.issueStatusId === newStatus.issueStatusId)
                                    .statusName === 'Pending'
                            )
                                this.incrementIssues();
                            else if (
                                this.statusOptions.find((x) => x.issueStatusId === tempOldStatusId).statusName ===
                                'Pending'
                            ) {
                                this.decrementIssues();
                            }
                        } catch (error) {
                            const message = 'There was an error updating this issue.';
                            showErrorMessage(this, message, error);
                        } finally {
                            this.$_handleLoaderState(false);
                        }
                    }
                });
        },
        closeOtherDropDown() {
            const dropdowns = document.querySelectorAll('.open');
            dropdowns.forEach((dropdown) => dropdown.classList.remove('open'));
        },
        getStatusClassName(statusId) {
            const status = this.statusOptions.find((x) => x.issueStatusId === statusId);
            return status.statusName.toLowerCase().replace(' ', '-');
        },
        getStatusName(statusId) {
            const status = this.statusOptions.find((x) => x.issueStatusId === statusId);
            return status.statusName;
        }
    }
};
</script>

<style scoped>
.status {
    color: #fff !important;
    min-width: 90px;
    text-align: center;
    display: block;
    line-height: 31px;
    font-weight: 400;
    max-width: 90px;
    cursor: pointer;
    border-radius: 3px;
}

.mobile-status {
    min-width: 32px !important;
    max-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 50% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    font-weight: 600 !important;
    font-size: 14px !important;
}

.mobile-dropdown {
    min-width: 120px !important;
    padding: 4px 0 !important;
}

.mobile-dropdown li a {
    padding: 8px 12px !important;
    font-size: 14px !important;
}

::v-deep .dropdown-menu {
    li {
        cursor: pointer !important;
    }
}

.no-pointer {
    cursor: not-allowed !important;
}

.status-button-container {
    position: relative;
    display: inline-block;
}

.status-button {
    display: flex;
    align-items: center;
    padding: 5px 10px;
    border-radius: 15px;
    font-size: 0.9em;
    font-weight: 500;
    color: white;
}

.status-options {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-top: 5px;
    min-width: 150px;
}

.status-option {
    padding: 8px 15px;
    cursor: pointer;
    color: white;
}

.status-option:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.status-option:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}

.status-option:hover {
    opacity: 0.9;
}
</style>
